import { ApiClient } from './api';

/**
 * Twilio can prove the browser hung up (`sdk_edge.disconnected_by = "local"`) but never
 * why — a deliberate `Device.disconnectAll()` is indistinguishable from a clean hangup
 * from outside this process. So each end is stamped with a cause here and posted to the
 * server to be logged; `EventLogger` does not reach New Relic.
 *
 * Shared by both softphones so a call ending on either UI produces a comparable row.
 */

export type EndReason = 'hangup' | 'disconnect' | 'cancel' | 'error';

/**
 * The server classifies anything outside `user-hangup` / `remote-hangup` /
 * `call-cancel` / `dial-failed` as an unexpected teardown, so those four spellings are
 * a wire contract — do not re-hyphenate them. `device-offline` is a voice-sdk 1.x event
 * name that should never fire on 2.x; no rows for it is expected, not a broken reporter.
 */
export type CallEndCause =
	| 'user-hangup'
	| 'pipeline-teardown'
	| 'legacy-teardown'
	| 'device-evicted'
	| 'device-offline'
	| 'device-error'
	| 'remote-hangup'
	| 'call-error'
	| 'call-cancel'
	| 'websocket-hangup'
	| 'page-unload'
	| 'dial-failed';

export type CallEvent = 'error' | 'reconnecting' | 'reconnected';

/** Structural rather than the SDK's `Call` so this module, and its tests, stay SDK-free. */
type ReportableCall = {
	parameters: Record<string, string>;
	customParameters: Map<string, string>;
	status: () => string;
};

type CallDirection = 'inbound' | 'outbound';

type ReportOptions = {
	errorCode?: number;
	deviceState?: string;
	deferToNextLoad?: boolean;
};

type CallEventDetails = {
	errorCode?: number;
	message?: string;
	durationMs?: number;
};

const TAB_ID_KEY = 'levitate.aida.tabId';
const DEFERRED_REPORT_KEY = 'levitate.aida.deferredCallEndReport';
const DEFERRED_REPORT_MAX_AGE_MS = 2 * 60 * 1000;
const PHONE_CALL_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Twilio's `Call.State.Open`, inlined to keep the voice SDK out of this module. */
const OPEN_STATUS = 'open';

const fallbackTabId = `tab-${Math.random().toString(36).slice(2, 10)}`;

/**
 * Two distinct ids against one `phoneCallId` prove more than one tab drove the call.
 * Shared with the recording-playback report so both rows carry the same tab identity.
 */
export function getTabId(): string {
	try {
		const existing = window.sessionStorage.getItem(TAB_ID_KEY);
		if (existing) return existing;
		window.sessionStorage.setItem(TAB_ID_KEY, fallbackTabId);
		return fallbackTabId;
	} catch {
		return fallbackTabId;
	}
}

/**
 * Per-Device bookkeeping behind the call-end diagnostic. Owned by one softphone and
 * shared across its teardown paths, which deliberately overlap: a controller reports
 * before `disconnectAll()` so the cause survives `destroy()`, and the `disconnect` that
 * follows would otherwise report the same end again.
 */
export class CallEndReporter {
	private readonly apiClient: ApiClient;
	private readonly reported = new WeakSet<ReportableCall>();
	/**
	 * Twilio sets the status to `closed` *before* it emits `disconnect`/`cancel`, so a
	 * handler reading `status()` can never tell the rep was mid-call — which is exactly
	 * the case this diagnostic exists to find. Tracked off `accept` instead.
	 */
	private readonly wentLive = new WeakSet<ReportableCall>();
	/**
	 * Keyed per call, not a single field: per-call listeners are never detached, so a
	 * superseded Call reporting late would otherwise take the newer call's direction.
	 */
	private readonly directions = new WeakMap<ReportableCall, CallDirection>();
	private pendingCause: CallEndCause | undefined;

	constructor(apiClient: ApiClient) {
		this.apiClient = apiClient;
		this.sendDeferredReport();
	}

	/** Also drops a cause stamped for a teardown that never completed, which would mislabel this call. */
	public trackCall(call: ReportableCall, direction: CallDirection): void {
		this.directions.set(call, direction);
		this.pendingCause = undefined;
	}

	public markLive(call: ReportableCall): void {
		this.wentLive.add(call);
	}

	public stampCause(cause: CallEndCause): void {
		this.pendingCause = cause;
	}

	public stampCauseIfUnset(cause: CallEndCause): void {
		this.pendingCause = this.pendingCause ?? cause;
	}

	public takeCause(fallback: CallEndCause): CallEndCause {
		const cause = this.pendingCause ?? fallback;
		this.pendingCause = undefined;
		return cause;
	}

	public report(call: ReportableCall, cause: CallEndCause, reason: EndReason, options: ReportOptions = {}): void {
		if (this.reported.has(call)) return;
		this.reported.add(call);

		const payload = this.buildReport(call, cause, reason, options);
		if (!payload) return;

		if (options.deferToNextLoad) {
			try {
				window.sessionStorage.setItem(DEFERRED_REPORT_KEY, JSON.stringify({ ...payload, savedAt: Date.now() }));
				return;
			} catch {
				/* diagnostics are best-effort */
			}
		}
		this.send(payload.phoneCallId, payload.report);
	}

	public reportStillOpen(call: ReportableCall, cause: CallEndCause, reason: EndReason): void {
		const payload = this.buildReport(call, cause, reason, {});
		if (payload) this.send(payload.phoneCallId, payload.report);
	}

	public reportCallEvent(call: ReportableCall, event: CallEvent, details: CallEventDetails = {}): void {
		const phoneCallId = call.customParameters.get('phoneCallId');
		if (!phoneCallId) return;

		try {
			void this.apiClient
				.reportClientCallEvent(phoneCallId, {
					event,
					callSid: call.parameters.CallSid ?? undefined,
					errorCode: details.errorCode,
					message: details.message,
					durationMs: details.durationMs,
					tabId: getTabId(),
					tabVisibility: typeof document === 'undefined' ? undefined : document.visibilityState,
				})
				.catch(() => {
					/* diagnostics are best-effort */
				});
		} catch {
			/* diagnostics are best-effort */
		}
	}

	private buildReport(call: ReportableCall, cause: CallEndCause, reason: EndReason, options: ReportOptions) {
		// The endpoint is keyed by it, and without it there is nothing to correlate to.
		const phoneCallId = call.customParameters.get('phoneCallId');
		if (!phoneCallId) return undefined;

		let callStatus: string | undefined;
		try {
			callStatus = call.status();
		} catch {
			callStatus = undefined;
		}

		const report: ClientCallEndReport = {
			callSid: call.parameters.CallSid ?? undefined,
			callStatus,
			cause,
			deviceState: options.deviceState,
			direction: this.directions.get(call),
			errorCode: options.errorCode,
			hadActiveCall: this.wentLive.has(call) || callStatus === OPEN_STATUS,
			reason,
			tabId: getTabId(),
			tabVisibility: typeof document === 'undefined' ? undefined : document.visibilityState,
		};
		return { phoneCallId, report };
	}

	private sendDeferredReport(): void {
		let stored: string | null = null;
		try {
			stored = window.sessionStorage.getItem(DEFERRED_REPORT_KEY);
			window.sessionStorage.removeItem(DEFERRED_REPORT_KEY);
		} catch {
			return;
		}
		if (!stored) return;

		try {
			const { phoneCallId, report, savedAt } = JSON.parse(stored) as Record<string, unknown>;
			if (typeof phoneCallId !== 'string' || !PHONE_CALL_ID.test(phoneCallId)) return;
			if (typeof savedAt !== 'number' || Date.now() - savedAt > DEFERRED_REPORT_MAX_AGE_MS) return;
			const parsed = parseDeferredReport(report);
			if (parsed) this.send(phoneCallId, parsed);
		} catch {
			/* diagnostics are best-effort */
		}
	}

	private send(phoneCallId: string, report: ClientCallEndReport): void {
		try {
			void this.apiClient.reportClientCallEnd(phoneCallId, report).catch(() => {
				/* diagnostics are best-effort */
			});
		} catch {
			/* diagnostics are best-effort */
		}
	}
}

type ClientCallEndReport = Parameters<ApiClient['reportClientCallEnd']>[1];

const optionalString = (value: unknown) => (typeof value === 'string' ? value : undefined);

function parseDeferredReport(value: unknown): ClientCallEndReport | undefined {
	if (!value || typeof value !== 'object') return undefined;
	const report = value as Record<string, unknown>;
	if (report.cause !== 'page-unload' || typeof report.reason !== 'string') return undefined;

	return {
		cause: report.cause,
		reason: report.reason,
		hadActiveCall: report.hadActiveCall === true,
		callSid: optionalString(report.callSid),
		callStatus: optionalString(report.callStatus),
		direction: optionalString(report.direction),
		deviceState: optionalString(report.deviceState),
		tabId: optionalString(report.tabId),
		tabVisibility: optionalString(report.tabVisibility),
		errorCode: typeof report.errorCode === 'number' ? report.errorCode : undefined,
	};
}
